import React, { useMemo } from 'react';

const AUD_DENOMINATIONS = [
  { value: 100, label: '$100', type: 'note', accent: '#143d60', tint: '#1f4d7a' },
  { value: 50, label: '$50', type: 'note', accent: '#b7791f', tint: '#d69e2e' },
  { value: 20, label: '$20', type: 'note', accent: '#c53030', tint: '#e53e3e' },
  { value: 10, label: '$10', type: 'note', accent: '#2b6cb0', tint: '#4299e1' },
  { value: 5, label: '$5', type: 'note', accent: '#6b46c1', tint: '#9f7aea' },
  { value: 2, label: '$2', type: 'coin', accent: '#9c4221', tint: '#f6ad55' },
  { value: 1, label: '$1', type: 'coin', accent: '#4a5568', tint: '#cbd5e0' },
  { value: 0.5, label: '50¢', type: 'coin', accent: '#2d3748', tint: '#a0aec0' },
  { value: 0.2, label: '20¢', type: 'coin', accent: '#1a202c', tint: '#94a3b8' },
  { value: 0.1, label: '10¢', type: 'coin', accent: '#1f2933', tint: '#cbd5e0' },
  { value: 0.05, label: '5¢', type: 'coin', accent: '#1c1f24', tint: '#d1d5db' },
];

const formatDisplayValue = (value) => {
  if (value >= 1) {
    return `$${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}`;
  }
  return `${Math.round(value * 100)}¢`;
};

const renderArtwork = (denomination) => {
  if (denomination.type === 'coin') {
    return (
      <svg viewBox="0 0 80 80" className="w-14 h-14">
        <circle cx="40" cy="40" r="34" fill={denomination.tint} stroke={denomination.accent} strokeWidth="4" />
        <text x="40" y="46" textAnchor="middle" fill={denomination.accent} fontSize="20" fontWeight="600">
          {formatDisplayValue(denomination.value)}
        </text>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 160 90" className="w-full h-16">
      <rect
        x="2"
        y="2"
        width="156"
        height="86"
        rx="10"
        fill={denomination.tint}
        stroke={denomination.accent}
        strokeWidth="4"
      />
      <text x="20" y="55" fill="#fff" fontSize="28" fontWeight="600">
        {formatDisplayValue(denomination.value)}
      </text>
      <text x="120" y="35" fill="#fff" fontSize="16" opacity="0.7">
        AU
      </text>
    </svg>
  );
};

const CashDenominationPad = ({ onAdd, counts = {}, onClear, disabled, showSummary = true }) => {
  const summary = useMemo(() => {
    return Object.entries(counts || {})
      .map(([value, count]) => {
        const numericValue = Number(value);
        return {
          value: numericValue,
          display: formatDisplayValue(numericValue),
          count,
          subtotal: numericValue * count,
        };
      })
      .filter((entry) => entry.count > 0)
      .sort((a, b) => b.value - a.value);
  }, [counts]);

  const padTotal = summary.reduce((acc, curr) => acc + curr.subtotal, 0);
  const hasSelection = summary.length > 0;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-700">Quick cash denominations</h4>
          <p className="text-xs text-slate-500">Tap a note or coin to add it to the cash tendered field.</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasSelection || disabled}
          className={`text-xs font-semibold px-3 py-1 rounded-full border ${
            hasSelection && !disabled
              ? 'text-slate-700 border-slate-400 hover:bg-white'
              : 'text-slate-300 border-slate-200 cursor-not-allowed'
          }`}
        >
          Clear
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {AUD_DENOMINATIONS.map((denomination) => (
          <button
            key={denomination.value}
            type="button"
            onClick={() => onAdd(denomination.value)}
            disabled={disabled}
            className={`rounded-lg bg-white shadow-sm border border-slate-200 hover:border-primary transition p-2 flex flex-col items-center ${
              disabled ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {renderArtwork(denomination)}
            <span className="text-sm font-medium text-slate-700 mt-2">{denomination.label}</span>
          </button>
        ))}
      </div>
      {showSummary && hasSelection && (
        <div className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-600 space-y-1">
          <div className="flex justify-between font-semibold text-slate-800 text-sm">
            <span>From denomination pad</span>
            <span>${padTotal.toFixed(2)}</span>
          </div>
          {summary.map((entry) => (
            <div key={entry.value} className="flex justify-between">
              <span>
                {entry.count} × {entry.display}
              </span>
              <span>${entry.subtotal.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CashDenominationPad;
